import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  FormControl,
  Button,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Box,
  Typography,
  InputAdornment,
  CircularProgress
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';
import { useNavigate } from 'react-router-dom';

// Project import
import { gridSpacing } from 'config.js';

const UserPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const navigate = useNavigate();

  // API Functions
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await fetch('https://api-sso-v2.eurekagroup.id/api/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      const mappedData = (result.data || result).map(user => ({
        ...user,
        id: user._id
      }));
      setData(mappedData);
      console.log('Users loaded:', result);
    } catch (error) {
      console.error('Error fetching users:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal memuat data user: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const deleteUser = async (id) => {
    try {
      const response = await fetch(`https://api-sso-v2.eurekagroup.id/api/users/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Remove from local state
      setData(prevData => prevData.filter(user => user.id !== id && user._id !== id));
      
      SwalConfig.success(
        'Berhasil Dihapus!',
        'Data user telah berhasil dihapus dari sistem.',
        2000
      );
    } catch (error) {
      console.error('Error deleting user:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal menghapus user: ${error.message}`
      });
    }
  };

  // Load data on component mount
  useEffect(() => {
    fetchUsers();
  }, []);

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleStatusFilterChange = (event) => {
    setStatusFilter(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDeleteClick = (id) => {
    SwalConfig.deleteConfirm('user ini').then((result) => {
      if (result.isConfirmed) {
        deleteUser(id);
      }
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'aktif':
        return 'success';
      case 'inaktif':
        return 'error';
      case 'blokir':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filteredData = (data || [])
    .filter(user => {
      if (!user) return false;
      const username = (user.username || '').toLowerCase();
      const email = (user.email || '').toLowerCase();
      const namaLengkap = (user.nama_lengkap || '').toLowerCase();
      const empId = (user.emp_id || '').toLowerCase();
      const search = (searchText || '').toLowerCase();

      const matchesSearch = username.includes(search) ||
                           email.includes(search) ||
                           namaLengkap.includes(search) ||
                           empId.includes(search);
      const matchesStatus = statusFilter === '' || user.is_active === statusFilter;
      
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => (a?.nama_lengkap || '').localeCompare(b?.nama_lengkap || ''));

  const uniqueStatuses = [...new Set((data || []).map(user => user?.is_active).filter(Boolean))];

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <PersonIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Manajemen User SSO
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Kelola data user SSO dengan mudah dan efisien
                </Typography>
              </Box>
            </Box>
          }
        action={
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              startIcon={<RefreshIcon />} 
              onClick={fetchUsers}
              disabled={loading}
              sx={{ 
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              {loading ? <CircularProgress size={20} /> : 'Refresh'}
            </Button>
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<AddIcon />} 
              onClick={() => navigate('/sso/user/add')}
              className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
              sx={{ 
                borderRadius: 2,
                px: 3,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600,
                '&:hover': {
                  transform: 'translateY(-2px)'
                }
              }}
            >
              ✨ Tambah User
            </Button>
          </Box>
        }
        sx={{ pb: 2 }}
      />
      <Divider />
      <CardContent>
        {/* Filter Section */}
        <Box sx={{ 
          mb: 3, 
          p: 3, 
          bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover', 
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'grey.200'
        }} className="bg-gradient-to-r from-gray-50 to-blue-50 border-gray-200 shadow-sm animate-slide-up">
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cari User"
                variant="outlined"
                value={searchText}
                onChange={handleSearchChange}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth variant="outlined" size="small">
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilterChange}
                  displayEmpty
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterIcon color="action" />
                    </InputAdornment>
                  }
                  sx={{
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.background.paper
                  }}
                >
                  <MenuItem value="">
                    <em>Semua Status</em>
                  </MenuItem>
                  {uniqueStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {filteredData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total User
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Table Section */}
        <Box sx={{ 
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden'
        }}>
          {loading ? (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center', 
              height: 400,
              flexDirection: 'column',
              gap: 2
            }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ color: 'text.secondary' }}>
                Memuat data user...
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: 600 }}>
                <Table stickyHeader>
                  <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    No
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    minWidth: 200
                  }}>
                    User
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Email
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Employee ID
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}>
                    Tanggal Dibuat
                  </TableCell>
                  <TableCell sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white', 
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textAlign: 'center'
                  }}>
                    Aksi
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((user, index) => (
                  <TableRow 
                    key={user.id}
                    sx={{ 
                      '&:hover': { 
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover',
                        transform: 'scale(1.001)',
                        transition: 'all 0.2s ease'
                      },
                      '&:nth-of-type(even)': {
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'action.hover'
                      }
                    }}
                  >
                    <TableCell sx={{ fontWeight: 500 }}>
                      {index + 1 + page * rowsPerPage}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar 
                          sx={{ 
                            width: 40, 
                            height: 40,
                            bgcolor: 'primary.main'
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {user.nama_lengkap}
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                            @{user.username}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {user.email}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.emp_id}
                        variant="outlined"
                        color="info"
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={user.is_active}
                        color={getStatusColor(user.is_active)}
                        size="small"
                        sx={{ fontWeight: 500 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {formatDate(user.create_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <IconButton 
                          onClick={() => navigate(`/sso/user/view/${user.id}`)} 
                          sx={{ 
                            color: 'success.main',
                            '&:hover': { 
                              bgcolor: 'success.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => navigate(`/sso/user/edit/${user.id}`)} 
                          sx={{ 
                            color: 'info.main',
                            '&:hover': { 
                              bgcolor: 'info.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          onClick={() => handleDeleteClick(user.id)} 
                          sx={{ 
                            color: 'error.main',
                            '&:hover': { 
                              bgcolor: 'error.light',
                              color: 'white',
                              transform: 'scale(1.1)'
                            },
                            transition: 'all 0.2s ease'
                          }}
                          size="small"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'grey.200',
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                }}
              />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
    </div>
  );
};

export default UserPage;
